/**
 * Feature-specific helpers for the payout timesheet dashboard.
 *
 * Shared hour/month formatters live in ``utils/timekeeping`` and ``utils/datetime``.
 */

/**
 * Returns whether a dashboard row should display the No Hrs status pill.
 *
 * @param {Object} timesheet - Dashboard timesheet row.
 * @param {boolean} quickBooksExported - Whether QuickBooks was exported for the week.
 * @returns {boolean} Whether the row is a no-hours exported-week placeholder.
 */
export function isNoHoursPayoutDashboardRow(timesheet, quickBooksExported = false) {
  return quickBooksExported &&
    timesheet.status === "pending" &&
    Number(timesheet.shiftCount) === 0;
}

/**
 * Returns whether a payroll week has at least one worker shift.
 *
 * @param {Object} week - Dashboard week payload from the API.
 * @returns {boolean} Whether the week should be shown on the dashboard.
 */
export function weekHasShifts(week) {
  if (Number(week?.summary?.totalShifts) > 0) {
    return true;
  }

  return (week?.timesheets || []).some(
    (timesheet) => Number(timesheet.shiftCount) > 0
  );
}

/**
 * Builds dashboard info-chip summary values from payout timesheet rows.
 *
 * @param {Array<Object>} timesheets - Timesheet rows included in info-chip totals.
 * @returns {Object} Summary counts and hour totals.
 */
export function getPayoutDashboardSummary(timesheets = []) {
  const projectIds = new Set(
    timesheets.flatMap((timesheet) => timesheet.projectIds || [])
  );

  return {
    timesheetCount: timesheets.length,
    totalWorkerHours: timesheets.reduce((total, row) => total + Number(row.workerHours || 0), 0),
    totalHoursLogHours: timesheets.reduce((total, row) => total + Number(row.hoursLogHours || 0), 0),
    totalPayoutHours: timesheets.reduce((total, row) => total + Number(row.payoutHours || 0), 0),
    totalShifts: timesheets.reduce((total, row) => total + Number(row.shiftCount || 0), 0),
    totalProjects: projectIds.size,
    pendingCount: timesheets.filter((row) => row.status === "pending").length,
    approvingCount: timesheets.filter((row) => row.status === "approving").length,
    approvedCount: timesheets.filter((row) => row.status === "approved").length,
    exportedCount: timesheets.filter((row) => row.status === "exported").length,
    editedCount: timesheets.filter((row) => row.status === "edited").length,
  };
}

/**
 * Returns status-chip metadata for a payroll week.
 *
 * @param {Object} statuses - Payroll status object from the API.
 * @returns {Array<{key: string, label: string, complete: boolean}>} Status chip items.
 */
export function getPayrollStatusItems(statuses = {}) {
  return [
    {
      key: "iifExported",
      label: "QuickBooks Exported",
      complete: Boolean(statuses.iifExported),
    },
    {
      key: "timesheetsEmailed",
      label: "Timesheets Emailed",
      complete: Boolean(statuses.timesheetsEmailed),
    },
    {
      key: "signInSheetsGenerated",
      label: "Sign-In Sheets Complete",
      complete: Boolean(statuses.signInSheetsGenerated),
    },
    {
      key: "safetyMeetingFormsGenerated",
      label: "Safety Meetings Complete",
      complete: Boolean(statuses.safetyMeetingFormsGenerated),
    },
  ];
}
